import {
    ChangeDetectorRef,
    Component,
    ElementRef,
    HostListener,
    Inject,
    OnInit,
    QueryList,
    ViewChild,
    ViewChildren,
} from '@angular/core';
import { MAT_DIALOG_DATA, MatDialog, MatDialogRef } from '@angular/material/dialog';
import { ChannelSearchDialogComponent } from '@components/channel-search-dialog/channel-search-dialog.component';
import {
    ConfirmationWindowComponent,
    ConfirmationWindowTypes,
} from '@components/confirmation-window/confirmation-window.component';
import { TranslateService } from '@ngx-translate/core';
import { AutosavePage } from '@service/auto-save-controller.service';
import { ElementConditionLogicService } from '@service/element-condition-logic.service';
import { ElementConditionService } from '@service/element-condition.service';
import { MatrixChannelService } from '@service/matrix-channel.service';
import { Channel } from 'icreate/common/models/channel';
import { ElementConditionViewData, Operator } from 'icreate/common/models/element-condition';
import { ElementConditionLogicViewData } from 'icreate/common/models/element-condition-logic';
import { LibraryComponent } from 'icreate/common/models/library-component';
import { matrixChannelMax, MatrixChannelViewData } from 'icreate/common/models/matrix-channel';
import { Vessel } from 'icreate/common/models/vessel';
import { lastValueFrom, take } from 'rxjs';

@Component({
    selector: 'app-editor-matrix-dialog',
    templateUrl: './editor-matrix-dialog.component.html',
    styleUrls: ['./editor-matrix-dialog.component.css'],
    standalone: false,
})
export class EditorMatrixDialogComponent implements OnInit {
    readonly MAX_MATRIX_CHANNEL_NUM = matrixChannelMax;
    readonly ONE_PAGE_DATA_NUM = 3;

    matrixChannelList: MatrixChannelViewData[] = [];

    selectedMatrixChannel: MatrixChannelViewData;
    selectedCondition: ElementConditionViewData;

    isLoading: boolean = false;
    isShowSearchChannelDialog: boolean = false;
    channelSearchNameText: string = '';
    channelSearchTagText: string = '';

    currentPage: number = 1;
    isOpenMatrixChannelOption: boolean = false;

    deleteMainMessage: string = '';
    deleteSubMessage: string = '';
    undoMessage: string = '';

    conditionList: ElementConditionViewData[] = [];

    prevMatrixChannelList: MatrixChannelViewData[] = [];
    prevConditionList: ElementConditionViewData[] = [];

    isErrorInLogic: boolean = false;

    optionsTop: string;
    optionsLeft: string;

    private onScrollFunction;

    @ViewChild('matrixChannelSearchDialog') matrixChannelSearchDialog: ElementRef;
    @ViewChild('contentContainer') contentContainer: ElementRef;
    @ViewChild('channelMoreOptions') channelMoreOptions: ElementRef;
    @ViewChild('deleteDialog') deleteDialog: ConfirmationWindowComponent;
    @ViewChildren('channelRows') channelRows: QueryList<ElementRef>;

    constructor(
        @Inject(MAT_DIALOG_DATA)
        public data: {
            vessel: Vessel;
            elementId: string;
        },
        private dialogRef: MatDialogRef<EditorMatrixDialogComponent>,
        private dialog: MatDialog,
        private translate: TranslateService,
        private elementConditionService: ElementConditionService,
        private elementConditionLogicService: ElementConditionLogicService,
        private matrixChannelService: MatrixChannelService,
        private cdr: ChangeDetectorRef
    ) {}

    @HostListener('document:click', ['$event'])
    clickOut(event: MouseEvent) {
        if (!this.channelMoreOptions?.nativeElement.contains(event.target) && this.isOpenMatrixChannelOption) {
            this.closeOpenMatrix();
            this.selectedMatrixChannel = undefined;
        }
    }

    async ngOnInit() {
        this.onScrollFunction = this.onScroll.bind(this);
        try {
            this.isLoading = true;

            const conditionList = await this.elementConditionService.getByElementId(this.data.elementId);
            const matrixChannel = await this.matrixChannelService.getByElementId(this.data.elementId);

            this.conditionList = [...conditionList];
            this.matrixChannelList = [
                ...matrixChannel.map(matrixChannel => ({
                    ...matrixChannel,
                    logicList: matrixChannel.logicList.map(logic => ({
                        ...logic,
                        channel: matrixChannel.channel,
                        isError: false,
                    })),
                })),
            ];
        } catch (error) {
            console.log(error);
        } finally {
            this.isLoading = false;
        }
    }

    getPageStart() {
        return (this.currentPage - 1) * this.ONE_PAGE_DATA_NUM;
    }

    getPageEnd() {
        return this.getPageStart() + this.ONE_PAGE_DATA_NUM;
    }

    getPageConditions() {
        return this.conditionList.slice(this.getPageStart(), this.getPageEnd());
    }

    getEmtpyArray() {
        const n = Math.max(0, this.ONE_PAGE_DATA_NUM - this.getPageConditions().length);
        return Array(n).fill(undefined);
    }

    isSaveButtonDisabled() {
        return this.isErrorInLogic;
    }

    close() {
        this.dialogRef.close();
    }

    async createMatrixChannel() {
        try {
            this.cancelUndoEvent();
            const dialogRef = this.dialog.open(ChannelSearchDialogComponent, {
                data: {
                    vesselId: this.data?.vessel?.id,
                    assignedChannel: null,
                    allowedTypesSet: undefined,
                    setShouldDisableTypes: false,
                    multipleSelection: false,
                    preSelectedChannelsCount: 0,
                    stopAutoSave: true,
                    autoSavePage: AutosavePage.MIMIC,
                },
            });
            const channelSelected: Channel = await lastValueFrom(dialogRef.afterClosed());

            this.isLoading = true;
            if (channelSelected) {
                let newLogicList = [];

                this.conditionList.forEach((_, index) => {
                    newLogicList.push({
                        id: null,
                        index: index,
                        elementId: this.data.elementId,
                        conditionId: null,
                        matrixChannelId: null,
                        channel: channelSelected,
                        analogOperator: '',
                        analogValue: '',
                        digitalString: '',
                        isError: false,
                        setToAlarm: false,
                    });
                });

                const createdMatrixChannel = {
                    id: null,
                    elementId: this.data.elementId,
                    index: this.matrixChannelList.length,
                    channelId: channelSelected.id,
                    channel: channelSelected,
                    logicList: newLogicList,
                };

                this.matrixChannelList = [...this.matrixChannelList, createdMatrixChannel];
            }
        } catch (error) {
            console.log(error);
        } finally {
            this.isLoading = false;
        }
    }

    createCondition() {
        this.cancelUndoEvent();
        this.conditionList = [
            ...this.conditionList,
            {
                id: null,
                elementId: this.data.elementId,
                index: this.conditionList.length,
                name: `Condition ${this.conditionList.length + 1}`,
                componentId: null,
                operator: '',
                component: undefined,
            },
        ];

        this.matrixChannelList = this.matrixChannelList.map(matrixChannel => {
            return {
                ...matrixChannel,
                logicList: [
                    ...matrixChannel.logicList,
                    {
                        id: null,
                        elementId: this.data.elementId,
                        conditionId: null,
                        matrixChannelId: null,
                        index: this.conditionList.length - 1,
                        channel: matrixChannel.channel,
                        analogOperator: '',
                        analogValue: '',
                        digitalString: '',
                        isError: false,
                        setToAlarm: false,
                    },
                ],
            };
        });
    }

    changeConditionLogic(logic: ElementConditionLogicViewData, selectedMatrixChannel: MatrixChannelViewData) {
        const findIndex = this.matrixChannelList.findIndex(
            matrixChannel => matrixChannel.index === selectedMatrixChannel.index
        );
        if (findIndex !== -1) {
            this.matrixChannelList[findIndex].logicList = [
                ...this.matrixChannelList[findIndex].logicList.slice(0, logic.index),
                logic,
                ...this.matrixChannelList[findIndex].logicList.slice(
                    logic.index + 1,
                    this.matrixChannelList[findIndex].logicList.length
                ),
            ];

            this.checkConditionLogicError();
        }
    }

    checkConditionLogicError() {
        for (const matrixChannel of this.matrixChannelList) {
            for (const logic of matrixChannel.logicList ?? []) {
                if (logic.isError) {
                    this.isErrorInLogic = true;
                    return;
                }
            }
        }
        this.isErrorInLogic = false;
    }

    selectConditionLogic(logic: ElementConditionLogicViewData) {
        this.cancelUndoEvent();
        this.selectedCondition = { ...this.conditionList[logic.index] };
        const findIndex = this.matrixChannelList.findIndex(
            matrixChannel => matrixChannel.channelId === logic.channel.id
        );
        if (findIndex !== -1) {
            this.selectedMatrixChannel = {
                ...this.matrixChannelList[findIndex],
            };
        } else {
            this.selectedMatrixChannel = undefined;
        }
    }

    unSelectedConditionLogic() {
        this.selectedCondition = undefined;
        this.selectedMatrixChannel = undefined;
    }

    openOption(matrixChannel: MatrixChannelViewData) {
        if (this.selectedMatrixChannel?.index === matrixChannel.index && this.isOpenMatrixChannelOption) {
            this.closeOpenMatrix();
            this.selectedMatrixChannel = undefined;
        } else {
            this.cancelUndoEvent();
            const element = this.contentContainer?.nativeElement;
            element.addEventListener('scroll', this.onScrollFunction);
            this.updatePosition(matrixChannel.index);
            this.isOpenMatrixChannelOption = true;
            this.selectedMatrixChannel = { ...matrixChannel };
        }
    }

    async deleteMatrixChannel(matrixChannel: MatrixChannelViewData) {
        try {
            this.deleteMainMessage = this.translate.instant('matrix.channel.delete.main.message', {
                channelName: matrixChannel?.channel.opsData.name,
            });
            this.deleteSubMessage = 'matrix.channel.delete.sub.message';

            this.deleteDialog.setVisible(true, true, AutosavePage.MIMIC);
            const option = await lastValueFrom(this.deleteDialog.onOptionSelected.pipe(take(1)));
            if (option === ConfirmationWindowTypes.SAVE) {
                const deleteToIndex = this.matrixChannelList.findIndex(
                    matrixCh => matrixCh.index === matrixChannel.index
                );

                if (deleteToIndex !== -1) {
                    this.createMatrixChannelUndoEvent(
                        deleteToIndex,
                        'matrix.dialog.matrix-channel.delete.undo-message'
                    );

                    this.matrixChannelList.splice(deleteToIndex, 1);
                    this.matrixChannelList = this.matrixChannelList.map((matrixChannel, index) => ({
                        ...matrixChannel,
                        index,
                    }));
                }
                this.closeOpenMatrix();
                this.selectedMatrixChannel = matrixChannel;
            }
        } catch (error) {
            console.log(error);
        }
    }

    async deleteCondition(condition: ElementConditionViewData) {
        try {
            this.deleteMainMessage = this.translate.instant('matrix.condition.delete.main.message', {
                conditionName: condition?.name,
            });

            this.deleteSubMessage = 'matrix.condition.delete.sub.message';
            this.deleteDialog.setVisible(true, true, AutosavePage.MIMIC);
            const option = await lastValueFrom(this.deleteDialog.onOptionSelected.pipe(take(1)));
            if (option === ConfirmationWindowTypes.SAVE) {
                const deleteToIndex = this.conditionList.findIndex(cond => cond.index === condition.index);

                if (deleteToIndex !== -1) {
                    this.createConditionUndoEvent(deleteToIndex, 'matrix.dialog.condition.delete.undo-message');

                    this.conditionList.splice(deleteToIndex, 1);
                    this.conditionList = this.conditionList.map((condition, index) => {
                        return {
                            ...condition,
                            index,
                        };
                    });

                    this.matrixChannelList = this.matrixChannelList.map(matrixChannel => {
                        return {
                            ...matrixChannel,
                            logicList: [
                                ...matrixChannel.logicList.slice(0, condition.index),
                                ...matrixChannel.logicList.slice(condition.index + 1, matrixChannel.logicList.length),
                            ].map((logic, index) => ({ ...logic, index })),
                        };
                    });
                }
                this.closeOpenMatrix();
            }
        } catch (error) {
            console.log(error);
        }
    }

    clearSetting(matrixChannel: MatrixChannelViewData) {
        const clearToIndex = this.matrixChannelList.findIndex(matrixCh => matrixCh.index === matrixChannel.index);

        if (clearToIndex !== -1) {
            this.createMatrixChannelUndoEvent(clearToIndex, 'matrix.dialog.matrix-channel.clear-setting.undo-message');

            this.matrixChannelList[clearToIndex].logicList = this.matrixChannelList[clearToIndex].logicList.map(
                (_, index) => {
                    return {
                        id: null,
                        index: index,
                        elementId: this.data.elementId,
                        conditionId: null,
                        matrixChannelId: null,
                        channel: this.matrixChannelList[clearToIndex].channel,
                        analogOperator: '',
                        analogValue: '',
                        digitalString: '',
                        isError: false,
                        setToAlarm: false,
                    };
                }
            );
        }

        this.closeOpenMatrix();
    }

    clearAllSetting(condition: ElementConditionViewData) {
        const clearToIndex = this.conditionList.findIndex(cond => cond.index === condition.index);

        if (clearToIndex !== -1) {
            this.createConditionUndoEvent(clearToIndex, 'matrix.dialog.condition.clear-setting.undo-message');

            this.matrixChannelList = this.matrixChannelList.map((matrixChannel, index) => {
                return {
                    ...matrixChannel,
                    logicList: [
                        ...matrixChannel.logicList.slice(0, clearToIndex),
                        {
                            id: null,
                            elementId: this.data.elementId,
                            index: clearToIndex,
                            conditionId: null,
                            matrixChannelId: null,
                            channel: this.matrixChannelList[index].channel,
                            analogOperator: '',
                            analogValue: '',
                            digitalString: '',
                            isError: false,
                            setToAlarm: false,
                        },
                        ...matrixChannel.logicList.slice(clearToIndex + 1, matrixChannel.logicList.length),
                    ],
                };
            });
        }

        this.closeOpenMatrix();
    }

    copyMatrixChannelToPrev() {
        this.prevMatrixChannelList = this.matrixChannelList.map(matrixChannel => ({
            ...matrixChannel,
            logicList: matrixChannel.logicList?.map(logicList => ({ ...logicList })) ?? [],
        }));
    }

    makeUndoMessage(message: string, params) {
        this.undoMessage = this.translate.instant(message, params);
    }

    conditionSetToAlarm(condition: ElementConditionViewData) {
        const updateToIndex = this.conditionList.findIndex(cond => cond.index === condition.index);

        if (updateToIndex !== -1) {
            this.createConditionUndoEvent(updateToIndex, 'matrix.dialog.condition.set-to-alarm.undo-message');

            this.matrixChannelList = this.matrixChannelList.map((matrixChannel, index) => {
                return {
                    ...matrixChannel,
                    logicList: [
                        ...matrixChannel.logicList.slice(0, updateToIndex),
                        {
                            id: null,
                            elementId: this.data.elementId,
                            index: updateToIndex,
                            conditionId: null,
                            matrixChannelId: null,
                            channel: this.matrixChannelList[index].channel,
                            analogOperator: '',
                            analogValue: '',
                            digitalString: '',
                            isError: false,
                            setToAlarm: true,
                        },
                        ...matrixChannel.logicList.slice(updateToIndex + 1, matrixChannel.logicList.length),
                    ],
                };
            });
        }

        this.closeOpenMatrix();
    }

    isNextButtonDisabled() {
        return this.currentPage >= this.conditionList.length / this.ONE_PAGE_DATA_NUM;
    }

    isPrevButtonDisabled() {
        return this.currentPage === 1;
    }

    goNextPage() {
        this.currentPage += 1;
    }

    goPrevPage() {
        this.currentPage -= 1;
    }

    selectCondition(condition: ElementConditionViewData) {
        this.cancelUndoEvent();
        this.selectedCondition = { ...condition };
    }

    selectComponent(event: { component: LibraryComponent; condition: ElementConditionViewData }) {
        this.cancelUndoEvent();
        const { component, condition } = event;
        const updateToIndex = this.conditionList.findIndex(cond => cond.index === condition.index);
        if (updateToIndex !== -1) {
            this.conditionList = [
                ...this.conditionList.slice(0, updateToIndex),
                {
                    ...condition,
                    componentId: component.id,
                    component: component,
                },
                ...this.conditionList.slice(updateToIndex + 1, this.conditionList.length),
            ];
        }
    }

    async save() {
        try {
            this.isLoading = true;

            const updatedElementCondition = await this.elementConditionService.update(
                this.conditionList.map(condition => ({
                    id: condition.id,
                    elementId: this.data.elementId,
                    componentId: condition.componentId ? condition.componentId : null,
                    index: condition.index,
                    operator: condition.operator as Operator,
                    name: condition.name,
                })),
                this.data.elementId
            );

            const updatedMatrixChannel = await this.matrixChannelService.update(
                this.matrixChannelList.map(matrixChannel => ({
                    id: matrixChannel.id,
                    elementId: this.data.elementId,
                    channelId: matrixChannel.channelId,
                    index: matrixChannel.index,
                })),
                this.data.elementId
            );

            const logicList = this.matrixChannelList.reduce((prev, matrixChannel, index) => {
                return [
                    ...prev,
                    ...matrixChannel.logicList.map(logic => ({
                        id: logic.id,
                        elementId: logic.elementId,
                        conditionId: updatedElementCondition[logic.index].id,
                        matrixChannelId: updatedMatrixChannel[index].id,
                        index: logic.index,
                        analogOperator: logic.analogOperator,
                        analogValue: logic.analogValue,
                        digitalString: logic.digitalString,
                        setToAlarm: logic.setToAlarm,
                    })),
                ];
            }, []);

            await this.elementConditionLogicService.update(logicList, this.data.elementId);
        } catch (error) {
            console.log(error);
        } finally {
            this.isLoading = false;
            this.close();
        }
    }

    createConditionUndoEvent(index: number, message: string) {
        this.prevConditionList = [...this.conditionList];
        this.copyMatrixChannelToPrev();
        this.makeUndoMessage(message, {
            name: this.conditionList[index].name,
        });
    }

    createMatrixChannelUndoEvent(index: number, message: string) {
        this.copyMatrixChannelToPrev();
        this.makeUndoMessage(message, {
            tag: this.matrixChannelList[index].channel.opsData.tag,
            name: this.matrixChannelList[index].channel.opsData.name,
        });
    }

    applyUndoEvent() {
        if (this.prevMatrixChannelList.length) {
            this.matrixChannelList = this.prevMatrixChannelList.map(matrixChannel => ({
                ...matrixChannel,
                logicList: matrixChannel.logicList?.map(logicList => ({ ...logicList })) ?? [],
            }));
            this.prevMatrixChannelList = [];
            this.selectedMatrixChannel = undefined;
        }
        if (this.prevConditionList.length) {
            this.conditionList = [...this.prevConditionList];
            this.selectedCondition = undefined;
        }

        this.undoMessage = '';
    }

    cancelUndoEvent() {
        if (this.prevMatrixChannelList.length) {
            this.prevMatrixChannelList = [];
            this.selectedMatrixChannel = undefined;
        }

        if (this.prevConditionList.length) {
            this.prevConditionList = [];
            this.selectedCondition = undefined;
        }

        this.undoMessage = '';
    }

    private updatePosition(index) {
        if (!this.channelRows) return;
        const matrixChannelsArray = this.channelRows.toArray();
        const selectedMatrixChannel = matrixChannelsArray ? matrixChannelsArray[index] : null;
        if (!selectedMatrixChannel) return;
        const matrixChannelPosition = selectedMatrixChannel.nativeElement.getBoundingClientRect();
        const scrollTop = window.scrollY;
        const scrollLeft = window.scrollX;

        let top = matrixChannelPosition.bottom + scrollTop - 20;

        this.optionsTop = top + 8 + 'px';
        this.optionsLeft = matrixChannelPosition.left + scrollLeft + 130 + 'px';
        this.cdr.detectChanges();
    }

    onScroll(event: Event): void {
        if (!this.channelRows || !this.isOpenMatrixChannelOption) return;
        this.closeOpenMatrix();
    }

    closeOpenMatrix() {
        const element = this.contentContainer?.nativeElement;
        element.removeEventListener('scroll', this.onScrollFunction);
        this.isOpenMatrixChannelOption = false;
    }
}
